import React from 'react'
import { CheckCircle, XCircle, ExternalLink, Users, Award } from 'lucide-react'

interface ResolutionModalProps {
  market: any
  evidence: any[]
  onClose: () => void
  onClaim?: () => void
  userWallet?: string | null // User's wallet address
}

export default function ResolutionModal({ market, evidence, onClose, onClaim, userWallet }: ResolutionModalProps) {
  const winningOutcome = market.outcome || market.winningOutcome
  const isDisputed = market.status === 'disputed'
  const isYesWin = winningOutcome === 1
  const isAgiMarket = market.is_manual === 1 || market.category === 'agi_manual'
  
  // Check if current user is a winner (only show claim button if they won)
  const [userIsWinner, setUserIsWinner] = React.useState(false)
  const [checkingWinner, setCheckingWinner] = React.useState(true)
  
  React.useEffect(() => {
    const checkIfUserWon = async () => {
      if (!userWallet || isDisputed) {
        setCheckingWinner(false)
        return
      }
      
      try {
        // Fetch user's bets on this market
        const response = await fetch(`/api/markets/${market.id}/bets`)
        const data = await response.json()
        const userBet = data.bets?.find((bet: any) => 
          bet.wallet_address?.toLowerCase() === userWallet.toLowerCase()
        )
        
        // User is winner if they bet on the winning side
        if (userBet && userBet.outcome === winningOutcome) {
          setUserIsWinner(true)
        }
      } catch (error) {
        console.error('Failed to check if user won:', error)
      } finally {
        setCheckingWinner(false)
      }
    }
    
    checkIfUserWon()
  }, [userWallet, market.id, winningOutcome, isDisputed])
  
  // Group evidence by validator and show only the latest from each
  const latestEvidenceByValidator = evidence.reduce((acc: any, ev: any) => {
    if (!acc[ev.validator_id] || new Date(ev.timestamp).getTime() > new Date(acc[ev.validator_id].timestamp).getTime()) {
      acc[ev.validator_id] = ev;
    }
    return acc;
  }, {});
  
  const uniqueEvidence = Object.values(latestEvidenceByValidator);
  
  // Map internal validator IDs to anonymous display names
  const getValidatorName = (validatorId: string) => {
    const mapping: Record<string, string> = {
      'AI_ADJUDICATOR': 'AGI ARX Adjudicator',
      'claude_opus': 'ARX Agent 1',
      'claude_sonnet': 'ARX Agent 2',
      'gpt4_web_search': 'ARX Agent 3',
      'gpt4_secondary': 'ARX Agent 4',
      'gpt35_turbo': 'ARX Agent 5',
      'perplexity_online': 'ARX Agent 6',
      'gemini_flash': 'ARX Agent 7',
      'mistral_large': 'ARX Agent 8',
      'CONTINUOUS_MONITOR': 'ARX Monitor'
    }
    return mapping[validatorId] || 'ARX Agent'
  }
  
  // Clean up source names to be user-friendly
  const cleanSourceName = (source: string) => {
    const cleanMapping: Record<string, string> = {
      'perplexity_search': 'Web Search',
      'perplexity_online': 'Web Search',
      'web_search': 'Web Search',
      'coingecko_price': 'Price Data API',
      'coinmarketcap_price': 'Price Data API',
      'birdeye_token_info': 'Token Data API',
      'dexscreener_token': 'DEX Data API',
      'espn_scores': 'Sports Data API',
      'twitter_search': 'Social Media API',
      'yahoo_finance': 'Financial Data API',
      'weather_api': 'Weather Data API',
      'news_search': 'News API'
    }
    
    // If it's a URL, keep it as is
    if (source && (source.includes('://') || source.includes('www.'))) {
      return source;
    }
    
    return cleanMapping[source] || source || 'Unknown Source';
  }
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#001233] border border-[#2AAAEA]/40 rounded-xl shadow-xl shadow-[#2AAAEA]/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`p-6 ${isDisputed ? 'bg-[#FBB03B]/10 border-[#FBB03B]/30' : (isYesWin ? 'bg-[#009245]/10 border-[#009245]/30' : 'bg-red-900/10 border-red-500/30')} border-b`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              {isDisputed ? (
                <Award className="w-8 h-8 text-cpx-yellow" />
              ) : isYesWin ? (
                <CheckCircle className="w-8 h-8 text-cpx-green" />
              ) : (
                <XCircle className="w-8 h-8 text-red-400" />
              )}
              <div>
                <h2 className={`text-2xl font-bold ${isDisputed ? 'text-cpx-yellow' : (isYesWin ? 'text-cpx-green' : 'text-red-400')}`}>
                  {isDisputed ? '⚠️ Awaiting Human Review' : '🎉 Market Resolved!'}
                </h2>
                <p className={`text-sm ${isDisputed ? 'text-gray-200' : (isYesWin ? 'text-gray-200' : 'text-gray-200')}`}>
                  {isDisputed ? (
                    <strong className="text-xl text-cpx-yellow">DISPUTED - Under Review 🔍</strong>
                  ) : (
                    <>Winning Outcome: <strong className={`text-xl ${isYesWin ? 'text-cpx-green' : 'text-red-400'}`}>{isYesWin ? 'YES ✅' : 'NO ❌'}</strong></>
                  )}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <XCircle className="w-6 h-6" />
            </button>
          </div>
          
          {market.adjudicator_reasoning && (
            <div className="mt-4 p-4 bg-[#052393]/20 rounded-lg border border-[#2AAAEA]/30">
              <h3 className="font-semibold text-cpx-light-blue mb-2">📊 Resolution Summary:</h3>
              <p className="text-white text-sm leading-relaxed">
                {market.adjudicator_reasoning
                  .replace(/Source:.*AI Oracle:.*$/gi, '')  // Remove entire "Source: AI Oracle:..." line
                  .replace(/AI Oracle:/gi, '')
                  .replace(/Claude Opus/gi, 'ARX Agent 1')
                  .replace(/Claude Sonnet/gi, 'ARX Agent 2')
                  .replace(/Claude/gi, 'ARX Agent')
                  .replace(/Perplexity/gi, 'ARX Agent 6')
                  .replace(/GPT-4/gi, 'ARX Agent')
                  .replace(/GPT/gi, 'ARX Agent')
                  .replace(/OpenAI/gi, 'ARX')
                  .replace(/Anthropic/gi, 'ARX')
                  .replace(/perplexity_search/gi, 'web search')
                  .replace(/perplexity/gi, 'web search')
                  .replace(/_search/gi, '')  // Remove leftover _search
                  .trim()}
              </p>
            </div>
          )}
        </div>

        {/* Evidence Panel - Only for non-AGI markets */}
        {!isAgiMarket && (
        <div className="p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center">
            <Award className="w-5 h-5 mr-2" />
            Validation Evidence
          </h3>
          
          {uniqueEvidence && uniqueEvidence.length > 0 ? (
            <div className="space-y-4">
              {uniqueEvidence.map((ev: any, i: number) => {
                // Safe JSON parsing - handle plain text evidence
                let evidenceData = ev.evidence
                let sources = ev.sources
                
                if (typeof ev.evidence === 'string') {
                  try {
                    evidenceData = JSON.parse(ev.evidence)
                  } catch (e) {
                    evidenceData = ev.evidence // Keep as plain text
                  }
                }
                
                if (typeof ev.sources === 'string') {
                  try {
                    sources = JSON.parse(ev.sources)
                  } catch (e) {
                    sources = [ev.sources] // Wrap plain text in array
                  }
                }
                
                return (
                  <div key={i} className="border border-[#052393]/40 bg-[#001a3d] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {ev.outcome === 'YES' ? (
                          <CheckCircle className="w-5 h-5 text-cpx-green" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-400" />
                        )}
                        <span className="font-semibold text-white">
                          {getValidatorName(ev.validator_id)}
                        </span>
                      </div>
                      <span className="text-sm text-cpx-light-blue">
                        Confidence: {ev.confidence}%
                      </span>
                    </div>
                    
                    <div className="ml-7 space-y-2">
                      <div>
                        <span className="text-sm font-medium text-gray-300">Decision:</span>
                        <span className={`ml-2 font-bold ${ev.outcome === 'YES' ? 'text-cpx-green' : 'text-red-400'}`}>
                          {ev.outcome}
                        </span>
                      </div>
                      
                      <div>
                        <span className="text-sm font-medium text-gray-300">Reasoning:</span>
                        <p className="text-sm text-gray-300 mt-1 whitespace-pre-wrap">{ev.reasoning || 'No reasoning provided'}</p>
                      </div>
                      
                      {/* Tool Details with URLs */}
                      {evidenceData.toolsUsed && evidenceData.toolsUsed.length > 0 && (
                        <div>
                          <span className="text-sm font-medium text-gray-300">Data Sources:</span>
                          <div className="mt-2 space-y-1">
                            {evidenceData.toolsUsed.map((tool: any, idx: number) => {
                              const cleanedToolName = cleanSourceName(tool.tool);
                              // Count how many times this tool was called
                              const sameToolCount = evidenceData.toolsUsed.filter((t: any) => t.tool === tool.tool).length;
                              const attemptNumber = evidenceData.toolsUsed.slice(0, idx + 1).filter((t: any) => t.tool === tool.tool).length;
                              
                              return (
                                <div key={idx} className="text-xs bg-[#052393]/20 border border-[#052393]/40 p-2 rounded-lg">
                                  <div className="font-semibold text-white">
                                    {cleanedToolName.includes('API') ? '📊' : '🔍'} {cleanedToolName}
                                    {sameToolCount > 1 && <span className="text-gray-400 ml-1">(Attempt {attemptNumber})</span>}
                                  </div>
                                  {tool.url && tool.url !== 'N/A' && (
                                    <a 
                                      href={tool.url.startsWith('http') ? tool.url : `https://${tool.url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-cpx-light-blue hover:text-[#2AAAEA] flex items-center space-x-1 mt-1 transition-colors"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      <span className="underline break-all text-xs">{tool.url}</span>
                                    </a>
                                  )}
                                  <div className={`${tool.success ? 'text-cpx-green' : 'text-red-400'}`}>
                                    {tool.success ? '✅ Success' : '❌ Failed'}
                                  </div>
                                  
                                  {/* Show actual data from the tool call */}
                                  {tool.success && tool.data && (
                                    <div className="mt-2 bg-[#001233] border border-[#2AAAEA]/20 p-2 rounded text-xs">
                                      <div className="font-medium text-cpx-light-blue mb-1">Data Found:</div>
                                      <div className="text-gray-200">
                                        {/* Price data */}
                                        {tool.data.price && (
                                          <div>💰 Price: ${typeof tool.data.price === 'number' ? tool.data.price.toLocaleString() : tool.data.price}</div>
                                        )}
                                        
                                        {/* Score data */}
                                        {tool.data.score && (
                                          <div>🏈 Score: {JSON.stringify(tool.data.score)}</div>
                                        )}
                                        
                                        {/* Game result */}
                                        {tool.data.result && (
                                          <div>🎯 Result: {tool.data.result}</div>
                                        )}
                                        
                                        {/* Winner */}
                                        {tool.data.winner && (
                                          <div>🏆 Winner: {tool.data.winner}</div>
                                        )}
                                        
                                        {/* Content/Summary - truncated, filter out garbage */}
                                        {tool.data.content && !String(tool.data.content).toLowerCase().includes('undefined') && 
                                         !String(tool.data.content).toLowerCase().includes('## definition') && (
                                          <div>📝 {String(tool.data.content).substring(0, 150)}{String(tool.data.content).length > 150 ? '...' : ''}</div>
                                        )}
                                        
                                        {/* Fallback: show first 100 chars of stringified data */}
                                        {!tool.data.price && !tool.data.score && !tool.data.result && !tool.data.winner && !tool.data.content && (
                                          <div className="truncate">{JSON.stringify(tool.data).substring(0, 100)}...</div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {evidenceData.price && (
                        <div>
                          <span className="text-sm font-medium text-gray-300">Price Found:</span>
                          <span className="ml-2 text-sm font-mono text-white">
                            ${evidenceData.price?.toLocaleString()}
                          </span>
                        </div>
                      )}
                      
                      {/* Enhanced Sources Display - Extract and Show All URLs */}
                      {(() => {
                        // Combine sources from multiple places
                        const allSources: string[] = [];
                        
                        // From sources array
                        if (sources && sources.length > 0) {
                          allSources.push(...sources);
                        }
                        
                        // Extract URLs from reasoning text
                        const reasoningUrls = (ev.reasoning || '').match(/https?:\/\/[^\s\)]+/g) || [];
                        allSources.push(...reasoningUrls);
                        
                        // Extract URLs from evidence data
                        if (evidenceData.sources && Array.isArray(evidenceData.sources)) {
                          allSources.push(...evidenceData.sources);
                        }
                        
                        // Deduplicate URLs
                        const uniqueUrls = Array.from(new Set(allSources));
                        
                        if (uniqueUrls.length === 0) return null;
                        
                        return (
                          <div>
                            <span className="text-sm font-medium text-gray-300">Sources:</span>
                            <div className="mt-1 space-y-1">
                              {uniqueUrls.map((source: string, idx: number) => {
                                // Clean AI provider names
                                if (!source) return null;
                                
                                let cleanedSource = source
                                  .replace(/AI Oracle:/gi, '')
                                  .replace(/Perplexity Search Tool Results/gi, 'Web Search Results')
                                  .replace(/Perplexity search results/gi, 'Web search results')  
                                  .replace(/Perplexity/gi, 'Web Search')
                                  .replace(/perplexity_search tool/gi, 'web search')
                                  .replace(/perplexity_search/gi, 'web search')
                                  .replace(/_search/gi, '')  // Remove leftover _search
                                  .replace(/Claude/gi, 'ARX Agent')
                                  .replace(/GPT-4/gi, 'ARX Agent')
                                  .replace(/GPT/gi, 'ARX Agent')
                                  .replace(/OpenAI/gi, 'ARX')
                                  .replace(/Anthropic/gi, 'ARX')
                                  .replace(/Gemini/gi, 'ARX Agent')
                                  .replace(/Google/gi, 'ARX')
                                  .trim();
                                
                                // Check if this is a URL (including www. without http)
                                const isDirectUrl = source.startsWith('http') || source.includes('://') || source.startsWith('www.');
                                const urlMatch = cleanedSource.match(/(https?:\/\/[^\s\)]+)/);
                                
                                if (isDirectUrl || urlMatch) {
                                  let url = isDirectUrl 
                                    ? (source.startsWith('http') ? source : `https://${source}`)
                                    : urlMatch[1];
                                  
                                  // Handle www. domains without http
                                  if (url.startsWith('www.')) {
                                    url = `https://${url}`;
                                  }
                                  
                                  // Extract domain for display
                                  const domain = url.match(/https?:\/\/([^\/]+)/)?.[1] || url;
                                  
                                  return (
                                    <a
                                      key={idx}
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs bg-blue-950/50 border border-blue-500/50 text-blue-300 px-2 py-1.5 rounded hover:bg-blue-900/50 hover:text-white flex items-center space-x-1 w-fit transition-colors"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                      <span className="underline truncate max-w-md">{domain}</span>
                                    </a>
                                  );
                                }
                                
                                // Check if it's a website mention (ESPN, Sky Sports, etc.)
                                const websiteMentions = ['ESPN', 'Sky Sports', 'BBC Sport', 'TNT Sports', 'Premier League', 'Yahoo Sports', 'Reuters', 'CBS Sports'];
                                const hasWebsiteMention = websiteMentions.some(site => cleanedSource.includes(site));
                                
                                if (hasWebsiteMention) {
                                  return (
                                    <span key={idx} className="text-xs bg-emerald-950/50 border border-emerald-500/50 text-emerald-300 px-2 py-1 rounded block font-medium">
                                      📰 {cleanedSource}
                                    </span>
                                  );
                                }
                                
                                // API or tool result
                                return (
                                  <span key={idx} className="text-xs bg-gray-800 text-gray-300 px-2 py-1 rounded block">
                                    🔧 {cleanedSource}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                      
                      {new Date(ev.timestamp).toLocaleString() && (
                        <div className="text-xs text-gray-200">
                          Checked: {new Date(ev.timestamp).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center text-gray-200 py-8">
              No detailed evidence available
            </div>
          )}
        </div>
        )}

        {/* Blockchain Proof - Only for non-AGI markets */}
        {!isAgiMarket && (
        <div className="p-6 bg-[#052393]/20 border-t border-[#052393]/40">
          <h3 className="font-bold text-cpx-light-blue mb-3">🔗 Blockchain Proof</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center space-x-2">
              <span className="text-gray-300">Market ID:</span>
              <span className="font-mono text-white">{market.id}</span>
            </div>
            {market.resolution_tx && (
              <div className="flex items-center space-x-2">
                <span className="text-gray-300">Resolution Tx:</span>
                <a 
                  href={`https://explorer.solana.com/tx/${market.resolution_tx}?cluster=devnet`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cpx-light-blue hover:text-white flex items-center space-x-1 transition-colors"
                >
                  <span className="font-mono text-xs">{market.resolution_tx.slice(0, 8)}...{market.resolution_tx.slice(-8)}</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Winners/Losers Section */}
        <div className="p-6 border-t border-[#052393]/40">
          <h3 className="font-bold text-white mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-cpx-light-blue" />
            💰 Payout Information
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className={`p-4 rounded-lg border-2 ${isYesWin ? 'bg-[#009245]/10 border-[#009245]' : 'bg-[#001a3d] border-[#052393]/40'}`}>
              <div className="text-sm text-gray-300 font-medium">YES Pool</div>
              <div className="text-2xl font-bold text-white">
                {isAgiMarket 
                  ? (market.total_yes_bets || 0).toLocaleString()
                  : (market.total_yes || market.totalYes || 0).toLocaleString()
                } {isAgiMarket ? 'credits' : 'tokens'}
              </div>
              <div className={`text-xs font-semibold mt-1 ${isYesWin ? 'text-cpx-green' : 'text-red-400'}`}>
                {isYesWin ? '✅ Winners' : '❌ Losers'}
              </div>
            </div>
            
            <div className={`p-4 rounded-lg border-2 ${!isYesWin ? 'bg-red-900/10 border-red-500' : 'bg-[#001a3d] border-[#052393]/40'}`}>
              <div className="text-sm text-gray-300 font-medium">NO Pool</div>
              <div className="text-2xl font-bold text-white">
                {isAgiMarket
                  ? (market.total_no_bets || 0).toLocaleString()
                  : (market.total_no || market.totalNo || 0).toLocaleString()
                } {isAgiMarket ? 'credits' : 'tokens'}
              </div>
              <div className={`text-xs font-semibold mt-1 ${!isYesWin ? 'text-cpx-green' : 'text-red-400'}`}>
                {!isYesWin ? '✅ Winners' : '❌ Losers'}
              </div>
            </div>
          </div>
          
          {/* Only show claim button if user actually won - NOT for AGI markets */}
          {onClaim && userIsWinner && !checkingWinner && !isAgiMarket && (
            <button
              onClick={onClaim}
              className="w-full bg-gradient-to-r from-[#009245] to-emerald-600 hover:shadow-lg hover:shadow-[#009245]/50 text-white text-lg py-4 rounded-xl font-bold transition-all"
            >
              💰 Claim Your Winnings from Blockchain
            </button>
          )}
          
          {/* AGI Markets: Credits are auto-distributed on resolution */}
          {isAgiMarket && (
            <div className="w-full bg-yellow-500/10 border border-yellow-500/30 text-center py-4 rounded-xl">
              <p className="text-yellow-400 font-semibold">✨ AGI Market: Credits were automatically distributed when the market was resolved.</p>
            </div>
          )}
          
          {/* Show message if user didn't win - NOT for AGI markets */}
          {!checkingWinner && userWallet && !userIsWinner && !isDisputed && !isAgiMarket && (
            <div className="w-full bg-[#052393]/20 border border-[#2AAAEA]/30 text-center py-4 rounded-xl">
              <p className="text-gray-300">You did not bet on the winning outcome for this market.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

